import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Store, MapPin, ExternalLink, Instagram, Globe, ArrowRight, Star, Crown, Zap, Monitor, Send } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const API = "/api";

const VendorCard = ({ vendor }) => {
  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className="group rounded-sm overflow-hidden bg-card border border-border card-hover"
      data-testid={`vendor-card-${vendor.id}`}
    >
      <div className="aspect-square relative">
        <img
          src={vendor.image_url || 'https://images.unsplash.com/photo-1513519245088-0e12902e35a6?w=800'}
          alt={vendor.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute top-3 left-3 flex gap-2">
          <span className="px-3 py-1 bg-primary text-primary-foreground text-xs font-medium rounded-sm">
            {vendor.category}
          </span>
          {vendor.subscription_tier === 'featured' && (
            <Badge className="bg-yellow-600/90 text-white text-[10px] gap-1 border-0"><Star size={10} />Featured</Badge>
          )}
          {vendor.subscription_tier === 'premium' && (
            <Badge className="bg-purple-600/90 text-white text-[10px] gap-1 border-0"><Crown size={10} />Premium</Badge>
          )}
          {vendor.subscription_tier === 'earlybird' && (
            <Badge className="bg-emerald-600/90 text-white text-[10px] gap-1 border-0"><Zap size={10} />Early Bird</Badge>
          )}
        </div>
        
        {/* Hover overlay */}
        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center gap-3">
          {vendor.website && (
            <Button
              size="sm"
              variant="secondary"
              className="gap-2"
              onClick={() => window.open(vendor.website, '_blank')}
            >
              <Globe size={14} />
              Website
            </Button>
          )}
          {vendor.social_links?.instagram && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => window.open(`https://instagram.com/${vendor.social_links.instagram.replace('@', '')}`, '_blank')}
            >
              <Instagram size={14} />
            </Button>
          )}
        </div>
      </div>
      
      <div className="p-4 space-y-2">
        <h3 className="font-heading text-lg font-bold">{vendor.name}</h3>
        <p className="text-sm text-muted-foreground line-clamp-2">{vendor.description}</p>
        
        {vendor.location && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <MapPin size={14} className="text-primary" />
            <span>{vendor.location}</span>
          </div>
        )}
      </div>
    </motion.div>
  );
};

const Vendors = () => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showWebsiteForm, setShowWebsiteForm] = useState(false);
  const [webFormLoading, setWebFormLoading] = useState(false);
  const [webForm, setWebForm] = useState({ name: '', email: '', phone: '', business: '', details: '' });

  useEffect(() => {
    fetchVendors();
  }, []);

  const fetchVendors = async () => {
    try {
      const response = await axios.get(`${API}/vendors`);
      setVendors(response.data);
    } catch (error) {
      console.error('Failed to fetch vendors:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleWebsiteInquiry = async () => {
    if (!webForm.name || !webForm.email || !webForm.business) {
      toast.error('Please fill in name, email, and business name');
      return;
    }
    setWebFormLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: webForm.name,
        email: webForm.email,
        phone: webForm.phone,
        subject: `Website Inquiry: ${webForm.business}`,
        message: `Website/Web Services Inquiry\n\nBusiness: ${webForm.business}\nPhone: ${webForm.phone || 'Not provided'}\n\n${webForm.details}`,
        category: 'website_inquiry',
      });
      toast.success("We got you! We'll be in touch soon.");
      setShowWebsiteForm(false);
      setWebForm({ name: '', email: '', phone: '', business: '', details: '' });
    } catch {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setWebFormLoading(false);
    }
  };

  const categories = ['all', ...new Set(vendors.map(v => v.category))];
  const filteredVendors = selectedCategory === 'all' 
    ? vendors 
    : vendors.filter(v => v.category === selectedCategory);

  return (
    <div className="space-y-8" data-testid="vendors-page">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-4"
      >
        <div className="flex items-center gap-2">
          <Store size={16} className="text-primary" />
          <span className="text-xs text-muted-foreground tracking-widest uppercase">Handmade in Minnesota</span>
        </div>
        
        <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight">Artisan Vendors</h1>
        <p className="text-muted-foreground max-w-2xl">
          Support local artisans and craftspeople. Discover unique handmade goods from the Twin Cities community.
        </p>
      </motion.div>

      {/* Business Registration CTA */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="rounded-xl border border-emerald-500/30 bg-gradient-to-r from-emerald-900/20 via-card to-teal-900/20 p-5 flex flex-col sm:flex-row items-center gap-4 sm:justify-between"
        data-testid="business-register-cta"
      >
        <div className="text-center sm:text-left">
          <p className="font-bold text-base">Own a local business?</p>
          <p className="text-sm text-muted-foreground">Get your locally owned and operated business in front of the Twin Cities community.</p>
        </div>
        <Link
          to="/pricing"
          className="shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-sm transition-colors"
          data-testid="business-register-link"
        >
          View Business Plans <ArrowRight size={16} />
        </Link>
      </motion.div>

      {/* Website Services CTA */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        onClick={() => setShowWebsiteForm(true)}
        className="rounded-xl border border-blue-500/30 bg-gradient-to-r from-blue-900/20 via-card to-cyan-900/20 p-5 flex flex-col sm:flex-row items-center gap-4 sm:justify-between cursor-pointer hover:border-blue-400/50 transition-colors"
        data-testid="website-cta"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-500/20 border border-blue-500/30 flex items-center justify-center">
            <Monitor size={20} className="text-blue-400" />
          </div>
          <div className="text-center sm:text-left">
            <p className="font-bold text-base">Need a website? We gotchu!</p>
            <p className="text-sm text-muted-foreground">Professional website creation & management for local businesses.</p>
          </div>
        </div>
        <span className="shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white font-bold text-sm transition-colors">
          Get Started <ArrowRight size={16} />
        </span>
      </motion.div>

      {/* Filter */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="flex items-center gap-4"
      >
        <Select value={selectedCategory} onValueChange={setSelectedCategory}>
          <SelectTrigger className="w-48" data-testid="category-filter">
            <SelectValue placeholder="Filter by category" />
          </SelectTrigger>
          <SelectContent>
            {categories.map((category) => (
              <SelectItem key={category} value={category}>
                {category === 'all' ? 'All Categories' : category}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <span className="text-sm text-muted-foreground">
          {filteredVendors.length} vendor{filteredVendors.length !== 1 ? 's' : ''} found
        </span>
      </motion.div>

      {/* Grid */}
      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {[...Array(8)].map((_, i) => (
            <Skeleton key={i} className="aspect-square" />
          ))}
        </div>
      ) : filteredVendors.length === 0 ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-16"
        >
          <Store size={48} className="mx-auto text-muted-foreground mb-4" />
          <h3 className="font-heading text-xl font-bold mb-2">No vendors found</h3>
          <p className="text-muted-foreground">Check back soon for more local artisans!</p>
        </motion.div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6"
        >
          {filteredVendors.map((vendor, index) => (
            <motion.div
              key={vendor.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 * index }}
            >
              <VendorCard vendor={vendor} />
            </motion.div>
          ))}
        </motion.div>
      )}

      {/* Website Inquiry Dialog */}
      <Dialog open={showWebsiteForm} onOpenChange={setShowWebsiteForm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Monitor size={20} className="text-blue-400" /> Need a website? We gotchu!</DialogTitle>
            <DialogDescription>Tell us about your business and we'll get back to you with a plan.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="w_name" className="text-xs">Your Name *</Label>
                <Input id="w_name" placeholder="Name" value={webForm.name} onChange={e => setWebForm({...webForm, name: e.target.value})} data-testid="web-form-name" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="w_email" className="text-xs">Email *</Label>
                <Input id="w_email" type="email" placeholder="you@email.com" value={webForm.email} onChange={e => setWebForm({...webForm, email: e.target.value})} data-testid="web-form-email" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="w_phone" className="text-xs">Phone</Label>
                <Input id="w_phone" type="tel" placeholder="(612) 555-0000" value={webForm.phone} onChange={e => setWebForm({...webForm, phone: e.target.value})} data-testid="web-form-phone" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="w_biz" className="text-xs">Business Name *</Label>
                <Input id="w_biz" placeholder="Your business" value={webForm.business} onChange={e => setWebForm({...webForm, business: e.target.value})} data-testid="web-form-business" />
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="w_details" className="text-xs">What do you need?</Label>
              <Textarea id="w_details" placeholder="New website, redesign, online store, social media help..." rows={3} value={webForm.details} onChange={e => setWebForm({...webForm, details: e.target.value})} data-testid="web-form-details" />
            </div>
            <Button onClick={handleWebsiteInquiry} disabled={webFormLoading} className="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold gap-2" data-testid="web-form-submit">
              {webFormLoading ? 'Sending...' : 'Send Inquiry'}
              {!webFormLoading && <Send size={16} />}
            </Button>
            <p className="text-[11px] text-center text-muted-foreground">We'll get back to you within 24 hours.</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Vendors;
